<script setup lang="ts">
import { ref } from 'vue'
import Overlay from '~/components/app/Overlay.vue'
import CreateDomaineForm from '~/components/Forms/Domaine/CreateDomaineForm.vue'
import CreateAgenceForm from '~/components/Forms/Agence/CreateAgenceForm.vue'
import CreateEquipeForm from '~/components/Forms/Equipe/CreateEquipeForm.vue'
import { OVERLAY_EQUIPE_DOMAINE_TITLE } from '~/composables/direction/page/useDirectionPageOverlays'

defineProps<{
  directionLib: string
  showOverlayDomaine: boolean
  showOverlayModifyDomaine: boolean
  modifyDomaineContext: { directionLib: string; domaineLib: string }
  showOverlayAgence: boolean
  defaultDomaineForAgence: string
  showOverlayEquipe: boolean
  showOverlayModifyEquipeDomaine: boolean
  showOverlayModifyEquipeAgence: boolean
  showOverlayModifyAgence: boolean
  modifyEquipeDomaineContext: { directionLib: string; domaineLib: string }
  modifyEquipeAgenceContext: { directionLib: string; domaineLib: string; ageLib: string }
  modifyAgenceContext: { directionLib: string; domaineLib: string; ageLib: string }
  equipeFormContext: {
    directionName: string
    domaineName: string
    agenceName: string
    defaultEquLib?: string
  }
  goToHrefModifyAgence: string | undefined
  goToHrefModifyEquipeDomaine: string | undefined
  goToHrefModifyEquipeAgence: string | undefined
  domainesForForm: Array<{ domLib: string; domLibLong: string | null; domDesc: string | null }>
  domainesWithAgencesForForm: Record<string, { dirLib: string; domLib: string; ageLib: string; ageLibLong?: string | null; ageDesc?: string | null }[]>
}>()

const domaineCreateHasUnsavedChanges = ref(false)
const domaineModifyHasUnsavedChanges = ref(false)
const agenceCreateHasUnsavedChanges = ref(false)
const agenceModifyHasUnsavedChanges = ref(false)
const equipeCreateHasUnsavedChanges = ref(false)
const equipeModifyDomaineHasUnsavedChanges = ref(false)
const equipeModifyAgenceHasUnsavedChanges = ref(false)

const emit = defineEmits<{
  refetch: []
  'close-overlay-domaine': []
  'close-overlay-modify-domaine': []
  'close-overlay-agence': []
  'close-overlay-equipe': []
  'close-overlay-modify-equipe-domaine': []
  'close-overlay-modify-equipe-agence': []
  'close-overlay-modify-agence': []
}>()
</script>

<template>
  <Overlay
    v-if="directionLib"
    :show="showOverlayDomaine"
    title="Créer un domaine"
    :has-unsaved-changes="domaineCreateHasUnsavedChanges"
    @close="emit('close-overlay-domaine')"
  >
    <CreateDomaineForm
      v-model:has-unsaved-changes="domaineCreateHasUnsavedChanges"
      :direction-name="directionLib"
      :domaines="domainesForForm"
      :show-overlay="showOverlayDomaine"
      @close="emit('close-overlay-domaine')"
      @refetch="emit('refetch')"
    />
  </Overlay>

  <Overlay
    v-if="modifyDomaineContext.directionLib && modifyDomaineContext.domaineLib"
    :show="showOverlayModifyDomaine"
    title="Modifier le domaine"
    :has-unsaved-changes="domaineModifyHasUnsavedChanges"
    @close="emit('close-overlay-modify-domaine')"
  >
    <CreateDomaineForm
      v-model:has-unsaved-changes="domaineModifyHasUnsavedChanges"
      :direction-name="modifyDomaineContext.directionLib"
      :domaines="domainesForForm"
      :is-editing="true"
      :domaine-to-edit="modifyDomaineContext.domaineLib"
      :show-overlay="showOverlayModifyDomaine"
      @close="emit('close-overlay-modify-domaine')"
      @refetch="emit('refetch')"
    />
  </Overlay>

  <Overlay
    v-if="directionLib"
    :show="showOverlayAgence"
    title="Créer une agence"
    :has-unsaved-changes="agenceCreateHasUnsavedChanges"
    @close="emit('close-overlay-agence')"
  >
    <CreateAgenceForm
      v-model:has-unsaved-changes="agenceCreateHasUnsavedChanges"
      :direction-name="directionLib"
      :domaines-with-agences="domainesWithAgencesForForm"
      :default-dom-lib="defaultDomaineForAgence"
      :show-overlay="showOverlayAgence"
      @close="emit('close-overlay-agence')"
      @refetch="emit('refetch')"
    />
  </Overlay>

  <Overlay
    v-if="modifyAgenceContext.directionLib && modifyAgenceContext.domaineLib && modifyAgenceContext.ageLib"
    :show="showOverlayModifyAgence"
    title="Modifier l'agence"
    :go-to-href="goToHrefModifyAgence"
    go-to-label="Voir l'agence"
    :has-unsaved-changes="agenceModifyHasUnsavedChanges"
    @close="emit('close-overlay-modify-agence')"
  >
    <CreateAgenceForm
      v-model:has-unsaved-changes="agenceModifyHasUnsavedChanges"
      :direction-name="modifyAgenceContext.directionLib"
      :domaines-with-agences="domainesWithAgencesForForm"
      :default-dom-lib="modifyAgenceContext.domaineLib"
      :initial-selected-agence="modifyAgenceContext.ageLib"
      :is-editing="true"
      :show-overlay="showOverlayModifyAgence"
      @close="emit('close-overlay-modify-agence')"
      @refetch="emit('refetch')"
    />
  </Overlay>

  <Overlay
    v-if="equipeFormContext.directionName && equipeFormContext.domaineName && equipeFormContext.agenceName"
    :show="showOverlayEquipe"
    :title="OVERLAY_EQUIPE_DOMAINE_TITLE"
    :has-unsaved-changes="equipeCreateHasUnsavedChanges"
    @close="emit('close-overlay-equipe')"
  >
    <CreateEquipeForm
      v-model:has-unsaved-changes="equipeCreateHasUnsavedChanges"
      :direction-name="equipeFormContext.directionName"
      :domaine-name="equipeFormContext.domaineName"
      :agence-name="equipeFormContext.agenceName"
      :default-equ-lib="equipeFormContext.defaultEquLib"
      :show-overlay="showOverlayEquipe"
      @close="emit('close-overlay-equipe')"
      @refetch="emit('refetch')"
    />
  </Overlay>

  <Overlay
    v-if="modifyEquipeDomaineContext.directionLib && modifyEquipeDomaineContext.domaineLib"
    :show="showOverlayModifyEquipeDomaine"
    title="Modifier CODIR"
    :go-to-href="goToHrefModifyEquipeDomaine"
    go-to-label="Voir le CODIR"
    :has-unsaved-changes="equipeModifyDomaineHasUnsavedChanges"
    @close="emit('close-overlay-modify-equipe-domaine')"
  >
    <CreateEquipeForm
      v-model:has-unsaved-changes="equipeModifyDomaineHasUnsavedChanges"
      :show-overlay="showOverlayModifyEquipeDomaine"
      :direction-name="modifyEquipeDomaineContext.directionLib"
      :domaine-name="modifyEquipeDomaineContext.domaineLib"
      :agence-name="modifyEquipeDomaineContext.domaineLib"
      :equipe-to-edit="`${modifyEquipeDomaineContext.directionLib}~${modifyEquipeDomaineContext.domaineLib}~${modifyEquipeDomaineContext.domaineLib}~${modifyEquipeDomaineContext.domaineLib}`"
      :is-editing="true"
      @close="emit('close-overlay-modify-equipe-domaine')"
      @refetch="emit('refetch')"
    />
  </Overlay>

  <Overlay
    v-if="modifyEquipeAgenceContext.directionLib && modifyEquipeAgenceContext.domaineLib && modifyEquipeAgenceContext.ageLib"
    :show="showOverlayModifyEquipeAgence"
    title="Modifier CODIR"
    :go-to-href="goToHrefModifyEquipeAgence"
    go-to-label="Voir le CODIR"
    :has-unsaved-changes="equipeModifyAgenceHasUnsavedChanges"
    @close="emit('close-overlay-modify-equipe-agence')"
  >
    <CreateEquipeForm
      v-model:has-unsaved-changes="equipeModifyAgenceHasUnsavedChanges"
      :show-overlay="showOverlayModifyEquipeAgence"
      :direction-name="modifyEquipeAgenceContext.directionLib"
      :domaine-name="modifyEquipeAgenceContext.domaineLib"
      :agence-name="modifyEquipeAgenceContext.ageLib"
      :equipe-to-edit="`${modifyEquipeAgenceContext.directionLib}~${modifyEquipeAgenceContext.domaineLib}~${modifyEquipeAgenceContext.ageLib}~${modifyEquipeAgenceContext.ageLib}`"
      :is-editing="true"
      @close="emit('close-overlay-modify-equipe-agence')"
      @refetch="emit('refetch')"
    />
  </Overlay>
</template>
